import apiClient from './apiClient';
import type {
    AttendanceSession,
    CheckInRequest,
    CheckOutRequest,
    UpdateAttendanceRequest,
    AttendanceListParams,
    AttendanceSummaryParams,
    BulkEditAttendanceRequest,
    AttendanceRule,
    UpdateAttendanceRuleRequest,
    CreateAttendanceRuleRequest,
    CheckInPolicy,
    CreateCheckInPolicyRequest,
    UpdateCheckInPolicyRequest,
} from '../types/attendance.types';

export type { AttendanceSession };

const BASE = '/v1/attendance/caregiver-attendance';

class AttendanceService {

    async checkIn(data: CheckInRequest): Promise<AttendanceSession> {
        const response = await apiClient.post(`${BASE}/check-in`, data);
        return response.data;
    }

    async autoCheckIn(facilityId: string, loginTime: string): Promise<AttendanceSession> {
        const response = await apiClient.post(`/v1/attendance/checkin?facilityId=${facilityId}&loginTime=${encodeURIComponent(loginTime)}`);
        return response.data;
    }

    async checkOut(attendanceId: string, data?: CheckOutRequest): Promise<AttendanceSession> {
        const response = await apiClient.post(`${BASE}/check-out/${attendanceId}`, data ?? {});
        return response.data;
    }

    async autoCheckOut(facilityId: string, payload: { caregiverId: string, date: string, logoutTime?: string, checkoutTime?: string, checkOutTime?: string, checkOut?: string }): Promise<AttendanceSession> {
        const response = await apiClient.post(`/v1/attendance/checkout?facilityId=${facilityId}`, payload);
        return response.data;
    }

    async getActiveSession(caregiverId: string): Promise<AttendanceSession | null> {
        const response = await apiClient.get(`${BASE}/active/${caregiverId}`);
        return response.data ?? null;
    }

    async getHistory(params: {
        caregiverId: string;
        facilityId?: string;
        startDate?: string;
        endDate?: string;
        page?: number;
        size?: number;
    }): Promise<any> {
        const response = await apiClient.get(`${BASE}/history`, { params });
        return response.data;
    }
   
    async listByFacilityAndDate(params: AttendanceListParams): Promise<any> {
        const response = await apiClient.get(`${BASE}/admin/list`, { params });
        return response.data;
    }

    /**
     * Correct / override an attendance record (manager action).
     */
    async updateRecord(data: UpdateAttendanceRequest): Promise<any> {
        const response = await apiClient.put(`${BASE}/admin/update`, data);
        return response.data;
    }

    /**
     * Correct / override multiple attendance records (Bulk manager action).
     */
    async bulkUpdate(data: BulkEditAttendanceRequest): Promise<any> {
        const response = await apiClient.post(`/v1/attendance/bulk-edit`, data);
        return response.data;
    }


    async getAdminSummary(params: AttendanceSummaryParams): Promise<any> {
        const response = await apiClient.get(`${BASE}/admin/summary`, { params });
        return response.data;
    }

    async getAttendanceRules(facilityId: string): Promise<AttendanceRule[]> {
        const response = await apiClient.get('/v1/attendance-rules', {
            params: { facilityId }
        });
        return response.data;
    }

    async updateAttendanceRule(ruleId: string, data: UpdateAttendanceRuleRequest): Promise<AttendanceRule> {
        const response = await apiClient.put(`/v1/attendance-rules/${ruleId}`, data);
        return response.data;
    }

    async createAttendanceRule(data: CreateAttendanceRuleRequest): Promise<AttendanceRule> {
        const response = await apiClient.post('/v1/attendance-rules', data);
        return response.data;
    }

    async deleteAttendanceRule(ruleId: string): Promise<void> {
        await apiClient.delete(`/v1/attendance-rules/${ruleId}`);
    }
    async getCheckInPolicies(facilityId: string): Promise<CheckInPolicy[]> {
        const response = await apiClient.get(`/v1/check-in-policies/${facilityId}`);
        return response.data;
    }

    async createCheckInPolicy(data: CreateCheckInPolicyRequest): Promise<CheckInPolicy> {
        const response = await apiClient.post('/v1/check-in-policies', data);
        return response.data;
    }

    async updateCheckInPolicy(id: string, data: UpdateCheckInPolicyRequest): Promise<CheckInPolicy> {
        const response = await apiClient.put(`/v1/check-in-policies/${id}`, data);
        return response.data;
    }

    async downloadReportExcel(startDate?: string, endDate?: string, facilityId?: string): Promise<Blob> {
        const params: any = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (facilityId) params.facilityId = facilityId;

        const response = await apiClient.get('/v1/attendance/report/date-range', {
            params,
            headers: {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
        });
        return response.data;
    }

    async downloadReportPdf(startDate?: string, endDate?: string, facilityId?: string): Promise<Blob> {
        const params: any = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (facilityId) params.facilityId = facilityId;

        const response = await apiClient.get('/v1/attendance/report/date-range-pdf', {
            params,
            headers: {
                'Accept': 'application/pdf'
            },
            responseType: 'blob'
        });
        return response.data;
    }

}

const attendanceService = new AttendanceService();
export default attendanceService;
